/**
 * BrowserFrame — Simple window frame for landing previews (no tilt).
 * Rounded corners, shadow, window bar with optional badge.
 * Use for scrollytelling previews; overflow is clipped only inside the frame.
 */

import * as React from "react";
import { cn } from "@/lib/utils";

type BrowserFrameProps = {
  badge?: string;
  className?: string;
  children: React.ReactNode;
};

export function BrowserFrame({ badge, className, children }: BrowserFrameProps) {
  return (
    <div className={cn("relative", className)}>
      <div className="relative overflow-hidden rounded-2xl border border-black/10 bg-white shadow-[0_30px_90px_-55px_rgba(2,6,23,0.28),0_12px_28px_-20px_rgba(2,6,23,0.14)] dark:border-white/10 dark:bg-[#0B0D10] dark:shadow-[0_60px_140px_-90px_rgba(0,0,0,0.80),0_20px_60px_-40px_rgba(0,0,0,0.55)]">
        {/* Window bar */}
        <div className="flex items-center gap-2 border-b border-black/10 px-4 py-2.5 dark:border-white/10">
          <div className="flex gap-1.5">
            <span className="h-2 w-2 rounded-full bg-black/20 dark:bg-white/20" />
            <span className="h-2 w-2 rounded-full bg-black/15 dark:bg-white/15" />
            <span className="h-2 w-2 rounded-full bg-black/10 dark:bg-white/10" />
          </div>
          {badge ? (
            <span className="ml-2 rounded-full border border-black/10 bg-black/5 px-2 py-0.5 text-[11px] font-medium text-black/70 dark:border-white/10 dark:bg-white/5 dark:text-white/70">
              {badge}
            </span>
          ) : null}
          <div className="ml-auto h-1.5 w-20 rounded-full bg-black/10 dark:bg-white/10" />
        </div>
        {/* Content: clip overflow only here, not on section */}
        <div className="relative overflow-hidden bg-black dark:bg-[#0B0D10]">
          {/* Content with soft mask (secret feel) */}
          <div className="relative z-0 [-webkit-mask-image:linear-gradient(to_bottom,transparent,black_12%,black_88%,transparent)] [mask-image:linear-gradient(to_bottom,transparent,black_12%,black_88%,transparent)]">
            {children}
          </div>
          {/* Vignette + top haze on top of content */}
          <div className="pointer-events-none absolute inset-0 z-10" aria-hidden>
            <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_20%,transparent_0%,rgba(0,0,0,0.08)_60%,rgba(0,0,0,0.16)_100%)] dark:bg-[radial-gradient(120%_80%_at_50%_20%,transparent_0%,rgba(0,0,0,0.35)_65%,rgba(0,0,0,0.55)_100%)]" />
            <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/35 to-transparent dark:from-black/35" />
          </div>
        </div>
      </div>
    </div>
  );
}
